import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, AlertCircle, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";

export default function ActivityTrackingWidget() {
  const { data: staleClaimsData, isLoading } = useQuery<{ staleClaims: any[]; count: number }>({
    queryKey: ["/api/claims/activity-tracking/stale", { minDays: "10" }],
  });

  const staleClaims = staleClaimsData?.staleClaims || [];
  const highPriorityCount = staleClaims.filter(c => c.tracking.followUpPriority === "high").length;
  const totalCount = staleClaims.length;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activity Tracking
          </CardTitle>
          <CardDescription>Claims needing follow-up</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Activity Tracking
        </CardTitle>
        <CardDescription>Claims needing follow-up</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold text-foreground" data-testid="widget-total-stale">
              {totalCount}
            </p>
            <p className="text-sm text-muted-foreground">Stale Claims</p>
          </div>
          {highPriorityCount > 0 && (
            <Badge variant="destructive" className="flex items-center gap-1" data-testid="widget-high-priority">
              <AlertCircle className="h-3 w-3" />
              {highPriorityCount} Urgent
            </Badge>
          )}
        </div>

        {totalCount > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {totalCount} claim{totalCount !== 1 ? 's' : ''} require{totalCount === 1 ? 's' : ''} immediate attention
            </p>
            <Link href="/activity-tracking">
              <Button variant="outline" className="w-full" data-testid="widget-view-all">
                View All Claims
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
              All claims up to date!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
